// Unified bulk-import wizard for Students / Teachers / Employees
// Reuses one page with `type` URL param
import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Upload, FileDown, FileSpreadsheet, CheckCircle2, AlertTriangle,
  KeyRound, Users, Copy, Eye, EyeOff, ArrowRight, GraduationCap, Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import { escapeHtml as esc } from "../lib/safeHtml";

const TYPES_STATIC = {
  students: {
    icon: GraduationCap, color: "emerald",
    template: "/students/import/template",
    upload: "/students/import",
    helpLinkPaths: ["stages", "grades", "classes"],
  },
  employees: {
    icon: Briefcase, color: "violet",
    template: "/employees/import/template",
    upload: "/employees/import",
    helpLinkPaths: ["subjects", "classes"],
  },
};

export default function BulkImportPage() {
  const { type = "students" } = useParams();
  const { t } = useI18n();
  const safeType = TYPES_STATIC[type] ? type : "employees";
  const cfgStatic = TYPES_STATIC[safeType];
  const cfg = {
    ...cfgStatic,
    label: safeType === "students" ? t("bi_students_label") : t("bi_employees_label"),
    createdLabel: safeType === "students" ? t("bi_created_students") : t("bi_created_employees"),
    parentLabel: safeType === "students" ? t("bi_parent_accounts") : t("bi_login_accounts"),
    note: safeType === "students" ? t("bi_note_students") : t("bi_note_employees"),
    helpLinks: cfgStatic.helpLinkPaths.map((p) => ({ to: `/${p}`, label: t(`bi_link_${p}`) })),
  };
  const Icon = cfg.icon;
  const nav = useNavigate();
  const fileRef = useRef(null);

  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showPw, setShowPw] = useState(true);

  useEffect(() => {
    setFile(null); setResult(null);
    api.get("/schools").then(r => {
      setSchools(r.data || []);
      if ((r.data || []).length) setSchoolId(r.data[0].id);
    });
  }, [type]);

  const downloadTemplate = async () => {
    if (!schoolId) { toast.error(t("sip_select_school_first")); return; }
    try {
      const res = await api.get(`${cfg.template}?school_id=${schoolId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${type}_template.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.endsWith(".xlsx")) { toast.error(t("sip_xlsx_only")); return; }
    setFile(f); setResult(null);
  };

  const upload = async () => {
    if (!file) { toast.error(t("sip_pick_excel_first")); return; }
    if (!schoolId) { toast.error(t("sip_pick_school")); return; }
    setBusy(true); setResult(null);
    const form = new FormData(); form.append("file", file);
    try {
      const { data } = await api.post(`${cfg.upload}?school_id=${schoolId}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const created = data.created_students ?? data.created_employees ?? 0;
      const acc = data.created_accounts ?? data.created_parents ?? 0;
      const teachers = data.created_teachers ?? 0;
      let msg = `${t("bi_import_success_a")} ${created} ${cfg.label} ${t("bi_import_success_and")} ${acc} ${t("bi_account_word")}`;
      if (teachers > 0) msg += ` (${t("bi_among_them")} ${teachers} ${t("bi_teacher_word")})`;
      toast.success(msg);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t("bi_copied"));
  };

  const printCredentials = () => {
    if (!result?.new_parent_credentials?.length) return;
    const html = `
      <html dir="rtl"><head><meta charset="utf-8"><title>${esc(t("bi_new_accounts_title"))}</title>
      <style>body{font-family:'Almarai','Tajawal',Arial,sans-serif;padding:30px}h1{color:#117A47}table{width:100%;border-collapse:collapse;margin-top:20px}th,td{padding:10px;border:1px solid #ccc;text-align:right}th{background:#117A47;color:white}tr:nth-child(even){background:#f5f5f5}.pw{font-family:'Courier New',monospace;font-weight:bold;background:#fffacd;padding:4px 8px;border-radius:4px}</style>
      </head><body>
      <h1>🔑 ${esc(t("bi_new_accounts_title"))} — ${esc(cfg.label)}</h1>
      <p>${esc(t("sip_print_total"))}: ${result.new_parent_credentials.length} ${esc(t("bi_account_word"))} · ${esc(new Date().toLocaleString())}</p>
      <table><tr><th>#</th><th>${esc(t("sip_role"))}</th><th>${esc(t("sip_name"))}</th><th>${esc(t("sip_email"))}</th><th>${esc(t("sip_password"))}</th></tr>
      ${result.new_parent_credentials.map((c, i) => `<tr><td>${i + 1}</td><td>${esc(c.role)}</td><td>${esc(c.name)}</td><td>${esc(c.email)}</td><td class="pw">${esc(c.password)}</td></tr>`).join("")}
      </table>
      <p style="margin-top:30px;color:#666;font-size:12px">${esc(t("bi_save_pw_warning"))}</p>
      </body></html>
    `;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked"); return; }
    w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 500);
  };

  const created = result?.created_students ?? result?.created_employees ?? 0;
  const accounts = result?.created_parents ?? result?.created_accounts ?? 0;
  const teachersCreated = result?.created_teachers ?? 0;
  const linked = result?.linked_parents ?? 0;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => nav(-1)} data-testid="import-back">
          <ArrowRight className="w-5 h-5 lucide-rotate-rtl" />
        </Button>
        <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
          <Icon className="w-7 h-7 text-primary" /> {t("bi_import_x_from_excel")} — {cfg.label}
        </h1>
      </div>

      {/* Type switcher tabs */}
      <Card className="p-2 border shadow-none">
        <div className="grid grid-cols-2 gap-1">
          {Object.entries(TYPES_STATIC).map(([key, tt]) => {
            const TIcon = tt.icon;
            const active = key === type;
            const lbl = key === "students" ? t("bi_students_label") : t("bi_employees_label");
            return (
              <Link key={key} to={`/import/${key}`} data-testid={`import-tab-${key}`}>
                <Button variant={active ? "default" : "ghost"} className="w-full h-12" size="sm">
                  <TIcon className="w-4 h-4 me-2" />
                  {lbl}
                </Button>
              </Link>
            );
          })}
        </div>
      </Card>

      {/* School selector */}
      <Card className="p-4 border shadow-none">
        <Label>{t("bi_target_school")}</Label>
        <Select value={schoolId} onValueChange={setSchoolId}>
          <SelectTrigger data-testid="import-school" className="mt-1"><SelectValue placeholder={t("sip_pick_school")} /></SelectTrigger>
          <SelectContent>
            {schools.map(s => <SelectItem key={s.id} value={s.id}>{s.name_ar || s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      {/* Instructions */}
      <Card className="p-5 border border-primary/30 bg-primary/5">
        <h2 className="font-semibold text-primary flex items-center gap-2 mb-3">
          <FileDown className="w-5 h-5" /> {t("bi_steps_title")}
        </h2>
        <ol className="space-y-2 text-sm list-decimal list-inside text-foreground/90">
          <li>{t("bi_step1")}</li>
          <li>{t("bi_step2")}</li>
          <li>{t("bi_step3")} — {cfg.label}</li>
          <li>{t("bi_step4")}</li>
          <li>{t("bi_step5")}</li>
        </ol>
        {cfg.note && (
          <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-900 rounded-lg text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <b>{t("bi_important")}:</b> {cfg.note}
              {cfg.helpLinks.length > 0 && (
                <span className="block mt-1">
                  {cfg.helpLinks.map((l, i) => (
                    <Link key={i} to={l.to} className="text-primary underline mx-1">{l.label}</Link>
                  ))}
                </span>
              )}
            </div>
          </div>
        )}
        <Button onClick={downloadTemplate} variant="outline" className="mt-4" data-testid="download-template" disabled={!schoolId}>
          <FileDown className="w-4 h-4 me-2" /> {t("bi_download_template")}
        </Button>
      </Card>

      {/* Upload area */}
      <Card className="p-6 border shadow-none space-y-4">
        <h2 className="font-semibold flex items-center gap-2">
          <Upload className="w-5 h-5 text-primary" /> {t("bi_upload_file")}
        </h2>
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-border hover:border-primary/60 rounded-xl p-8 text-center cursor-pointer smooth bg-muted/30"
          data-testid="import-dropzone"
        >
          <FileSpreadsheet className="w-12 h-12 mx-auto text-primary mb-3" />
          {file ? (
            <><p className="font-medium">{file.name}</p><p className="text-xs text-muted-foreground mt-1">{(file.size / 1024).toFixed(1)} KB</p></>
          ) : (
            <><p className="font-medium">{t("bi_drop_or_select")}</p><p className="text-xs text-muted-foreground mt-1">.xlsx</p></>
          )}
          <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={handleFile} data-testid="import-file-input" />
        </div>
        <Button onClick={upload} disabled={busy || !file} className="w-full h-12 text-base" data-testid="import-submit">
          {busy ? t("bi_uploading") : (<><Upload className="w-4 h-4 me-2" /> {t("bi_step_upload")}</>)}
        </Button>
      </Card>

      {/* Results */}
      {result && (
        <Card className="p-6 border-2 border-emerald-500/40 bg-emerald-500/5 space-y-5" data-testid="import-result">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            <div>
              <h2 className="text-xl font-bold">{t("bi_op_done")}</h2>
              <p className="text-xs text-muted-foreground">{t("sip_results_summary")}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBlock label={cfg.createdLabel} value={created} color="emerald" icon={cfg.icon} />
            <StatBlock label={cfg.parentLabel} value={accounts} color="primary" icon={KeyRound} />
            {teachersCreated > 0 && <StatBlock label={t("bi_teachers_linked")} value={teachersCreated} color="blue" icon={GraduationCap} />}
            {linked > 0 && <StatBlock label={t("sip_linked_existing")} value={linked} color="blue" icon={Users} />}
            <StatBlock label={t("sip_skipped")} value={result.skipped} color={result.skipped > 0 ? "amber" : "muted"} icon={AlertTriangle} />
          </div>

          {result.new_parent_credentials?.length > 0 && (
            <div className="border-t border-emerald-500/30 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <KeyRound className="w-4 h-4 text-primary" /> حسابات جديدة أُنشئت ({result.new_parent_credentials.length})
                </h3>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-300 rounded-lg p-3 mb-3 text-xs text-emerald-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>تم إرسال رابط "تعيين كلمة المرور" إلى بريد كل حساب. لا تُعرض كلمات المرور لأسباب أمنية.</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-primary/10 text-primary">
                    <tr>
                      <th className="p-2 text-start">#</th><th className="p-2 text-start">{t("bi_col_role")}</th>
                      <th className="p-2 text-start">{t("bi_col_name")}</th><th className="p-2 text-start">{t("bi_col_email")}</th>
                      <th className="p-2 text-start">حالة الرابط</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.new_parent_credentials.map((c, i) => (
                      <tr key={i} className="border-b border-border hover:bg-muted/50">
                        <td className="p-2 font-mono text-xs">{i + 1}</td>
                        <td className="p-2">{c.role}</td><td className="p-2">{c.name}</td>
                        <td className="p-2 font-mono text-xs">{c.email}</td>
                        <td className="p-2">
                          {c.reset_link_sent
                            ? <span className="text-emerald-700 dark:text-emerald-400">✓ أُرسل الرابط</span>
                            : <span className="text-amber-600">⚠ تعذّر الإرسال</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result.errors?.length > 0 && (
            <div className="border-t border-emerald-500/30 pt-4">
              <h3 className="font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" /> {t("sip_rows_with_errors")} ({result.errors.length})
              </h3>
              <ul className="space-y-1 text-sm">
                {result.errors.map((e, i) => (
                  <li key={i} className="text-amber-700 dark:text-amber-400">
                    <span className="font-mono">{t("sip_row_short")} {e.row}:</span> {e.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-emerald-500/30">
            <Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>{t("sip_import_another")}</Button>
            {type === "students" && <Button variant="outline" onClick={() => nav("/parents")}><Users className="w-4 h-4 me-2" /> {t("parents_title")}</Button>}
            {type === "employees" && (
              <>
                <Button variant="outline" onClick={() => nav("/hr/employees")}>{t("bi_view_employees")}</Button>
                {teachersCreated > 0 && <Button variant="outline" onClick={() => nav("/teachers")}><GraduationCap className="w-4 h-4 me-2" /> {t("teachers")}</Button>}
              </>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

function StatBlock({ icon: Icon, label, value, color }) {
  const colors = {
    emerald: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
    primary: "bg-primary/10 text-primary border-primary/30",
    blue: "bg-blue-500/10 text-blue-600 border-blue-500/30",
    amber: "bg-amber-500/10 text-amber-600 border-amber-500/30",
    muted: "bg-muted text-muted-foreground border-border",
  };
  return (
    <div className={`p-4 rounded-lg border ${colors[color]}`}>
      <Icon className="w-5 h-5 mb-2" />
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}
